import { useMutation } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  updateSetting,
  SettingData,
  SettingProps,
} from '@/account/useUserSetting';
import { useSession } from '@/auth/index';
import { useAppConfigContext } from '@/context/AppConfig';
import { CookiesStorage } from '@/storage/index';
import { useRedirect } from '@/utils/hooks/useRedirect';
import {
  NEXT_ACTION,
  PREVIOUS_ACTION,
  DELAY_SHOW_DURATION,
  DELAY_HIDE_DURATION,
} from './constants';
import { usePostOnboardingAnswers } from '../bowtie/helpers';
import {
  OnboardingQuestionsProps,
  QuestionProps,
  AnswersProps,
} from '../index';

export const useOnboardingWizard = (
  initialQuestions: OnboardingQuestionsProps
) => {
  const [planId] = useState<string>(initialQuestions.question_plan);
  const [questions] = useState<QuestionProps[]>(initialQuestions.questions);
  const [activeQuestion, setActiveQuestion] = useState<QuestionProps>(
    initialQuestions.questions[0]
  );
  const [progressBarPercentage, setProgressBarPercentage] = useState<number>(
    (initialQuestions.questions[0].screen / initialQuestions.questions.length) *
      100
  );
  const [displayBackBtn, setDisplayBackBtn] = useState<boolean>(false);
  const [questionSwitching, setQuestionSwitching] = useState<boolean>(false);
  const [answersStorage, setAnswersStorage] = useState<AnswersProps[]>([]);
  const { logError, routingUtils, trackEvent } = useAppConfigContext();
  const [isReadyForSubmit, setIsReadyForSubmit] = useState<boolean>(false);
  const { redirect } = useRedirect();
  const { location } = routingUtils;
  const {
    session: { account },
    setSession,
  } = useSession();
  const { postAnswers } = usePostOnboardingAnswers();
  const userSettingQuery = useMutation<SettingData, Error, SettingProps>({
    mutationFn: updateSetting,

    onSuccess: () => {
      // NOTE: Because this won't be null at this point of the flow
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      account!.account.user_settings.dqs_completed = true;

      setSession({ account });
    },
    onError: (error) => {
      logError('User Setting Update', error);
    },
  });

  useEffect(() => {
    // To prevent a race condition with the submit of the answers
    // and the setState of the answersStorage
    // we rely on this state, when this is true, the storage is updated
    if (isReadyForSubmit) {
      _onDQSComplete();
      setIsReadyForSubmit(false);
      _postAnswers();
    }
  }, [isReadyForSubmit]);

  const storeQuestionAnswer = (answer: AnswersProps) => {
    trackEvent(
      activeQuestion.page_header.toLowerCase(),
      'navigation click',
      'dqs onboarding'
    );
    if (answer.response && _isLastQuestion()) {
      _updateAnswersStorage(answer);
      setIsReadyForSubmit(true);
      return;
    }
    if (!answer.response || _isLastQuestion()) {
      _translateQuestionToTop();
      setIsReadyForSubmit(true);
      return;
    }

    _updateAnswersStorage(answer);
    _nextQuestion();
  };

  const _onDQSComplete = () => {
    sessionStorage.setItem('dqs_completed', 'true');
    _setUserSettingsDqsCompleted();
  };

  const _postAnswers = () => {
    postAnswers({
      user_code: account?.account.user_info.user_code,
      plan_id: planId,
      answers: answersStorage,
    });
  };

  const skipOnboardingQuestions = () => {
    trackEvent(
      activeQuestion.page_header.toLowerCase(),
      'skip',
      'dqs onboarding'
    );
    _onDQSComplete();
    if (answersStorage.length) _postAnswers();
    else _redirectToNextPage();
  };

  const _setUserSettingsDqsCompleted = () =>
    userSettingQuery.mutate({
      setting: 'dqs_completed',
      state: true,
    });

  const _redirectToNextPage = () => {
    const cookiesStorage = new CookiesStorage();
    const first_report_navigate = cookiesStorage.getValue(
      'first_report_navigate'
    );
    if (first_report_navigate) {
      cookiesStorage.removeValue('first_report_navigate');
      redirect(first_report_navigate);
    } else {
      const locationState = location.state as { next?: string };
      redirect(locationState?.next || '/dashboard');
    }
  };

  const _identifyQuestion = (screenId: number): QuestionProps =>
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    questions.find((question) => question.screen === screenId)!;

  const _isFirstQuestion = () =>
    questions[0].screen_id === activeQuestion.screen_id;

  const _isLastQuestion = () =>
    questions[questions.length - 1].screen_id === activeQuestion.screen_id;

  const _nextQuestion = () => {
    setDisplayBackBtn(true);
    _translateQuestionToTop();
    _hideAndShowNextQuestion(NEXT_ACTION);
  };

  const prevQuestion = () => {
    if (_isFirstQuestion()) return;

    _translateQuestionToTop();
    _hideAndShowNextQuestion(PREVIOUS_ACTION);
  };

  const _updateAnswersStorage = (answer: AnswersProps) => {
    setAnswersStorage((prevAnswersStorage) => {
      const existingAnswerIndex = prevAnswersStorage.findIndex(
        (storedAnswer) => storedAnswer?.screen_id === answer.screen_id
      );

      if (existingAnswerIndex !== -1) {
        prevAnswersStorage.splice(existingAnswerIndex, 1);
      }

      return [...prevAnswersStorage, answer];
    });
  };

  // HTML Manipulation Functions
  const _translateQuestionToTop = () => {
    setQuestionSwitching(true);
    const element = document.getElementById(
      `container-${activeQuestion.screen_id}`
    );
    if (element) {
      const transitionHeightTop = 620;
      const totalHeight = transitionHeightTop + element.offsetHeight;
      element.style.transform = `translateY(-${totalHeight}px)`;
    }
  };

  const _hideAndShowNextQuestion = (action: string) => {
    const element = document.getElementById(
      `container-${activeQuestion.screen_id}`
    );
    if (!element) return;

    setTimeout(() => {
      _hideAndRestoreStyleHtmlElement(element);

      const questionToRenderScreen =
        action === NEXT_ACTION
          ? activeQuestion.screen + 1
          : activeQuestion.screen - 1;

      const questionToRender = _identifyQuestion(questionToRenderScreen);
      const isBackBtnVisible =
        action === PREVIOUS_ACTION
          ? questions[0].screen_id !== questionToRender.screen_id
          : true;

      setActiveQuestion(questionToRender);
      setProgressBarPercentage(
        (questionToRenderScreen / questions.length) * 100
      );
      setDisplayBackBtn(isBackBtnVisible);
      _showHtmlElement(element);
    }, DELAY_HIDE_DURATION);
  };

  const _hideAndRestoreStyleHtmlElement = (element: HTMLElement) => {
    if (element) {
      element.classList.add('d-none');
      element.style.transform = 'translateY(-0px)';
    }
  };

  const _showHtmlElement = (element: HTMLElement) => {
    if (element) {
      setTimeout(() => {
        element.classList.remove('d-none');
        setQuestionSwitching(false);
      }, DELAY_SHOW_DURATION);
    }
  };

  return {
    prevQuestion,
    displayBackBtn,
    questionSwitching,
    activeQuestion,
    storeQuestionAnswer,
    progressBarPercentage,
    skipOnboardingQuestions,
    dqsCompleted: account?.account.user_settings.dqs_completed || false,
  };
};
